import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Plus, Search, Printer, Download, Settings, ChevronDown, MoreHorizontal } from 'lucide-react';
import { money, fmtDate } from '../money';
import { Combo } from './FormFrame';

const TITLES = { coa: 'Chart of accounts', customers: 'Customers', vendors: 'Vendors', products: 'Products & services', banktxns: 'Bank transactions' };
const NEWLABEL = { coa: 'New account', customers: 'New customer', vendors: 'New vendor', products: 'New product or service', banktxns: 'Add transaction' };

const ROWS_PER_PAGE = [25, 50, 100, 'All'];

function csvValue(c, r) {
  const v = r[c.key];
  if (c.money) return (v == null ? '' : (v / 100).toFixed(2));
  if (c.csv) return c.csv(r);
  if (v == null) return '';
  return String(v);
}

function downloadCsv(filename, columns, rows) {
  const esc = (s) => `"${String(s).replace(/"/g, '""')}"`;
  const head = columns.map((c) => esc(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => esc(csvValue(c, r))).join(',')).join('\n');
  const blob = new Blob([`${head}\n${body}`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}

// Mailing list (desktop File ▸ Export ▸ Addresses) + Avery 5160 labels (File ▸ Print Forms ▸ Labels).
function MailingButtons({ base, kind }) {
  const get = async (fmt) => {
    const r = await axios.get(`${base}/mailing-list`, { params: { kind, fmt }, responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    if (fmt === 'labels') { window.open(url, '_blank'); return; }
    const a = document.createElement('a'); a.href = url; a.download = `${kind}_addresses.csv`; a.click();
  };
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => get('csv')} title="Names and mailing addresses (CSV)" data-testid="list-mailing-csv">Mailing list</Button>
      <Button variant="outline" size="sm" onClick={() => get('labels')} title="Avery 5160 address labels (PDF)" data-testid="list-labels">Labels</Button>
    </>
  );
}

// Generic OL Finance list page: title, New + More, search + filters, table, row actions,
// and a WORKING settings gear (columns show/hide, rows per page, include inactive, compact)
// plus working print + export icons.
function ListPage({ view, isDark, title, newLabel, onNew, filters, search, setSearch, columns, rows,
  rowActions, exportHref, children, hasInactive, includeInactive, setIncludeInactive, selectable, batchActions, headerExtra }) {
  const [more, setMore] = useState(false);
  const [gear, setGear] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  const [compact, setCompact] = useState(false);
  const [perPage, setPerPage] = useState(50);
  const [page, setPage] = useState(0);
  const [hidden, setHidden] = useState({});
  const [sel, setSel] = useState({});
  const rowKey = (r, i) => r.id || r.name_id || r.account_id || i;
  const card = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';
  const cell = compact ? 'p-1.5' : 'p-2.5';

  const visCols = columns.filter((c) => !hidden[c.key]);
  const size = perPage === 'All' ? rows.length : perPage;
  const pageCount = Math.max(1, Math.ceil(rows.length / (size || 1)));
  const cur = Math.min(page, pageCount - 1);
  const shown = perPage === 'All' ? rows : rows.slice(cur * size, cur * size + size);
  useEffect(() => { setPage(0); }, [search, perPage, rows.length]);

  return (
    <div className="space-y-3" data-testid={`online-list-${view}`}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">{title}</h2>
        <div className="flex items-center gap-2">
          {headerExtra}
          <Button variant="outline" size="icon" title="Export to CSV" onClick={() => downloadCsv(`${view}.csv`, visCols, rows)} data-testid="list-export"><Download className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" title="Print" onClick={() => window.print()} data-testid="list-print"><Printer className="h-4 w-4" /></Button>
          <div className="relative">
            <Button variant="outline" size="icon" title="Settings" onClick={() => setGear((v) => !v)} data-testid="list-settings"><Settings className="h-4 w-4" /></Button>
            {gear && (
              <div className="absolute right-0 top-11 z-40 w-64 rounded-lg border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-xl p-3 text-sm space-y-3" data-testid="list-settings-menu">
                <div>
                  <p className="font-semibold mb-1">Columns</p>
                  <div className="grid grid-cols-2 gap-1">
                    {columns.map((c) => (
                      <label key={c.key} className="flex items-center gap-1.5 cursor-pointer" data-testid={`list-col-toggle-${c.key}`}>
                        <input type="checkbox" checked={!hidden[c.key]} onChange={() => setHidden((h) => ({ ...h, [c.key]: !h[c.key] }))} />
                        <span>{c.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <label className="flex items-center justify-between">
                  <span>Rows per page</span>
                  <select className="border rounded bg-transparent px-2 py-1" value={perPage} onChange={(e) => setPerPage(e.target.value === 'All' ? 'All' : Number(e.target.value))} data-testid="list-rows-per-page">
                    {ROWS_PER_PAGE.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                {hasInactive && (
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="list-include-inactive">
                    <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
                    <span>Include inactive</span>
                  </label>
                )}
                <label className="flex items-center gap-2 cursor-pointer" data-testid="list-compact-toggle">
                  <input type="checkbox" checked={compact} onChange={(e) => setCompact(e.target.checked)} />
                  <span>Compact rows</span>
                </label>
                {exportHref && <a href={exportHref} target="_blank" rel="noreferrer" className="block text-emerald-600 hover:underline" data-testid="list-export-server">Export full list (server)</a>}
              </div>
            )}
          </div>
          <div className="relative flex">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-r-none" onClick={onNew} data-testid="list-new"><Plus className="h-4 w-4 mr-1" />{newLabel}</Button>
            <button className="bg-emerald-700 hover:bg-emerald-800 text-white px-2 rounded-r-md border-l border-emerald-500" onClick={() => setMore((v) => !v)} data-testid="list-more"><ChevronDown className="h-4 w-4" /></button>
            {more && (
              <div className="absolute right-0 top-10 z-30 w-44 rounded-lg border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-xl" data-testid="list-more-menu">
                <button className="block w-full text-left px-3 py-2 text-sm hover:bg-emerald-600/10" onClick={() => { setMore(false); downloadCsv(`${view}.csv`, visCols, rows); }}>Export to CSV</button>
                <button className="block w-full text-left px-3 py-2 text-sm hover:bg-emerald-600/10" onClick={() => { setMore(false); window.print(); }}>Print list</button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-xs flex-1"><Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400" />
          <input className="w-full h-9 pl-8 pr-2 rounded border border-slate-300 dark:border-slate-600 bg-transparent text-sm" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="list-search" /></div>
        {filters}
      </div>
      {children}
      {selectable && batchActions && Object.values(sel).some(Boolean) && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm" data-testid="list-batch-bar">
          <span className="text-slate-600">{Object.values(sel).filter(Boolean).length} selected</span>
          {batchActions(rows.filter((r, i) => sel[rowKey(r, i)])).map((a) => (
            <Button key={a.label} size="sm" variant="outline" onClick={() => { a.onClick(); setSel({}); }} data-testid={`list-batch-${a.label.replace(/\s/g, '')}`}>{a.label}</Button>
          ))}
        </div>
      )}
      <div className={`rounded-lg border overflow-hidden ${card}`}>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500 border-b border-slate-200 dark:border-slate-700">
            {selectable && <th className={`${cell} w-8`}><input type="checkbox" checked={shown.length > 0 && shown.every((r, i) => sel[rowKey(r, cur * size + i)])} onChange={(e) => { const c = e.target.checked; setSel((s) => { const n = { ...s }; shown.forEach((r, i) => { n[rowKey(r, cur * size + i)] = c; }); return n; }); }} data-testid="list-check-all" /></th>}
            {visCols.map((c) => <th key={c.key} className={`${cell} ${c.money ? 'text-right' : ''}`}>{c.label}</th>)}
            {rowActions && <th className={`${cell} w-10`} />}
          </tr></thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={rowKey(r, i)} className="border-b border-slate-100 dark:border-slate-800 hover:bg-emerald-500/5" data-testid={`list-row-${i}`}>
                {selectable && <td className={cell}><input type="checkbox" checked={!!sel[rowKey(r, cur * size + i)]} onChange={(e) => setSel((s) => ({ ...s, [rowKey(r, cur * size + i)]: e.target.checked }))} data-testid={`list-check-${i}`} /></td>}
                {visCols.map((c) => <td key={c.key} className={`${cell} ${c.money ? 'text-right tabular-nums' : ''}`}>{c.render ? c.render(r) : (r[c.key] ?? '')}</td>)}
                {rowActions && (
                  <td className={`${cell} relative text-right`}>
                    <button onClick={() => setOpenRow(openRow === i ? null : i)} data-testid={`list-rowaction-${i}`}><MoreHorizontal className="h-4 w-4 text-slate-400" /></button>
                    {openRow === i && (
                      <div className="absolute right-6 top-6 z-30 w-40 rounded-lg border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-xl text-left">
                        {rowActions(r).map((a) => <button key={a.label} className="block w-full text-left px-3 py-2 text-sm hover:bg-emerald-600/10" onClick={() => { setOpenRow(null); a.onClick(); }} data-testid={`list-ra-${i}-${a.label.replace(/\s/g, '')}`}>{a.label}</button>)}
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={visCols.length + 1} className={`${cell} text-slate-500`} data-testid="list-empty">Nothing here yet.</td></tr>}
          </tbody>
        </table>
        {perPage !== 'All' && pageCount > 1 && (
          <div className="flex items-center justify-between px-3 py-2 text-sm border-t border-slate-200 dark:border-slate-700" data-testid="list-pager">
            <span className="text-slate-500">{rows.length} rows · page {cur + 1} of {pageCount}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={cur === 0} onClick={() => setPage(cur - 1)} data-testid="list-prev">Prev</Button>
              <Button size="sm" variant="outline" disabled={cur >= pageCount - 1} onClick={() => setPage(cur + 1)} data-testid="list-next">Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function OnlineList({ view, base, isDark, go }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [acctId, setAcctId] = useState('');
  const [bankAccts, setBankAccts] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(true);

  useEffect(() => {
    setSearch('');
    if (view === 'coa') axios.get(`${base}/accounts`).then((r) => setRows(r.data?.accounts || [])).catch(() => setRows([]));
    else if (view === 'customers') axios.get(`${base}/customers?scope=all`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
    else if (view === 'vendors') axios.get(`${base}/vendors?scope=all`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
    else if (view === 'products') axios.get(`${base}/lists/items`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
    else if (view === 'banktxns') axios.get(`${base}/accounts`).then((r) => {
      const b = (r.data?.accounts || []).filter((a) => ['Bank', 'Credit Card'].includes(a.type));
      setBankAccts(b); setAcctId((p) => p || b[0]?.account_id || '');
    }).catch(() => {});
  }, [base, view]);

  useEffect(() => {
    if (view === 'banktxns' && acctId) axios.get(`${base}/bank-register/${acctId}?sort=date`).then((r) => setRows(r.data?.rows || r.data || [])).catch(() => setRows([]));
  }, [base, view, acctId]);

  const q = search.toLowerCase();
  const filt = (txt) => !q || String(txt || '').toLowerCase().includes(q);
  const keepActive = (r) => includeInactive || r.is_active !== false;
  const openBal = (r) => money(r.open_balance != null ? r.open_balance : (r.balance || 0));
  const byName = (key) => (a, b) => String((key ? a[key] : a) || '').localeCompare(String((key ? b[key] : b) || ''), undefined, { sensitivity: 'base', numeric: true });

  if (view === 'coa') {
    const data = rows.filter((a) => filt(a.full_name)).filter(keepActive);
    return <ListPage view={view} isDark={isDark} title={TITLES.coa} newLabel={NEWLABEL.coa} onNew={() => go('form', 'account')}
      search={search} setSearch={setSearch} hasInactive includeInactive={includeInactive} setIncludeInactive={setIncludeInactive}
      columns={[
        { key: 'name', label: 'Name', csv: (r) => r.full_name || r.name, render: (r) => <span style={{ paddingLeft: `${((r.full_name || '').split(':').length - 1) * 14}px` }}>{r.name}</span> },
        { key: 'type', label: 'Type' }, { key: 'number', label: 'Number', render: (r) => r.number || '' },
        { key: 'balance', label: 'Balance', money: true, render: (r) => (r.balance == null ? '' : money(r.balance)) },
        { key: 'status', label: 'Status', csv: (r) => (r.is_active === false ? 'Inactive' : 'Active'), render: (r) => (r.is_active === false ? 'Inactive' : 'Active') },
      ]}
      rows={data}
      rowActions={(r) => [{ label: 'Edit', onClick: () => go('form', 'account') }, { label: 'Run report', onClick: () => go('reports') }]} />;
  }
  if (view === 'customers') {
    const data = rows.filter((c) => filt(c.display_name || c.name)).filter(keepActive)
      .sort((a, b) => byName()(a.display_name || a.name, b.display_name || b.name));
    const ob = (r) => (r.open_balance != null ? r.open_balance : (r.balance || 0));
    const today = new Date().toISOString().slice(0, 10);
    const openSum = data.reduce((s, r) => s + ob(r), 0);
    const overdueSum = data.filter((r) => r.oldest_due && r.oldest_due < today).reduce((s, r) => s + ob(r), 0);
    const withOpen = data.filter((r) => ob(r) !== 0).length;
    const bar = (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="customers-money-bar">
        {[['unbilled', 'Unbilled', 0, 'estimates'], ['overdue', 'Overdue', overdueSum, `${data.filter((r) => r.oldest_due && r.oldest_due < today).length} customers`],
          ['open', 'Open balances', openSum, `${withOpen} customers`], ['paid', 'Recently paid', 0, 'last 30 days']].map(([k, l, amt, sub]) => (
          <div key={k} className={`rounded-lg border p-3 ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`} data-testid={`customers-bar-${k}`}>
            <div className="text-xs text-slate-500">{l}</div><div className="text-lg font-semibold tabular-nums">{money(amt)}</div><div className="text-xs text-slate-400">{sub}</div>
          </div>
        ))}
      </div>
    );
    return <ListPage view={view} isDark={isDark} title={TITLES.customers} newLabel={NEWLABEL.customers} onNew={() => go('form', 'customer')}
      headerExtra={<MailingButtons base={base} kind="customer" />}
      search={search} setSearch={setSearch} hasInactive includeInactive={includeInactive} setIncludeInactive={setIncludeInactive}
      selectable batchActions={(rowsSel) => [
        { label: `Create invoice (${rowsSel.length})`, onClick: () => go('form', 'invoice') },
        { label: 'Make inactive', onClick: () => Promise.all(rowsSel.map((r) => axios.put(`${base}/names/${r.name_id}`, { is_active: false }).catch(() => {}))).then(() => axios.get(`${base}/customers?scope=all`).then((x) => setRows(x.data || []))) }]}
      columns={[{ key: 'name', label: 'Customer', csv: (r) => r.display_name || r.name, render: (r) => r.display_name || r.name },
        { key: 'company', label: 'Company' }, { key: 'email', label: 'Email' }, { key: 'phone', label: 'Phone' },
        { key: 'open_balance', label: 'Open balance', money: true, csv: (r) => ((r.open_balance != null ? r.open_balance : (r.balance || 0)) / 100).toFixed(2), render: openBal }]}
      rows={data}
      rowActions={(r) => [{ label: 'Create invoice', onClick: () => go('form', 'invoice') }, { label: 'Receive payment', onClick: () => go('form', 'payment') }]}>
      {bar}
    </ListPage>;
  }
  if (view === 'vendors') {
    const data = rows.filter((v) => filt(v.name)).filter(keepActive).sort(byName('name'));
    return <ListPage view={view} isDark={isDark} title={TITLES.vendors} newLabel={NEWLABEL.vendors} onNew={() => go('form', 'vendor')}
      headerExtra={<MailingButtons base={base} kind="vendor" />}
      search={search} setSearch={setSearch} hasInactive includeInactive={includeInactive} setIncludeInactive={setIncludeInactive}
      columns={[{ key: 'name', label: 'Vendor' }, { key: 'company', label: 'Company' }, { key: 'email', label: 'Email' }, { key: 'phone', label: 'Phone' },
        { key: 'open_balance', label: 'Open balance', money: true, csv: (r) => ((r.open_balance != null ? r.open_balance : (r.balance || 0)) / 100).toFixed(2), render: openBal }]}
      rows={data}
      rowActions={(r) => [{ label: 'Create bill', onClick: () => go('form', 'bill') }, { label: 'Create expense', onClick: () => go('form', 'expense') }]} />;
  }
  if (view === 'products') {
    const data = rows.filter((it) => filt(it.name)).sort(byName('name'));
    return <ListPage view={view} isDark={isDark} title={TITLES.products} newLabel={NEWLABEL.products} onNew={() => go('form', 'item')}
      search={search} setSearch={setSearch} exportHref={`${base}/export/csv/items`}
      columns={[{ key: 'name', label: 'Name' }, { key: 'type', label: 'Type', render: (r) => r.type || 'Service' }, { key: 'account', label: 'Income account', render: (r) => r.income_account || r.account || '' }]}
      rows={data} />;
  }
  // banktxns
  const data = rows.filter((r) => filt(r.name) || filt(r.memo));
  return <ListPage view={view} isDark={isDark} title={TITLES.banktxns} newLabel={NEWLABEL.banktxns} onNew={() => go('form', 'expense')}
    search={search} setSearch={setSearch}
    filters={<div className="w-56"><Combo value={acctId} onChange={setAcctId} options={bankAccts.map((a) => ({ id: a.account_id, label: a.full_name }))} placeholder="Account" testid="banktxns-account" /></div>}
    columns={[{ key: 'date', label: 'Date', render: (r) => fmtDate(r.date) }, { key: 'num', label: 'Ref no.', render: (r) => r.num || r.ref || '' }, { key: 'name', label: 'Payee', render: (r) => r.name || r.payee || '' },
      { key: 'memo', label: 'Memo' }, { key: 'payment', label: 'Payment', money: true, render: (r) => (r.payment ? money(r.payment) : '') },
      { key: 'deposit', label: 'Deposit', money: true, render: (r) => (r.deposit ? money(r.deposit) : '') },
      { key: 'balance', label: 'Balance', money: true, render: (r) => (r.balance == null ? '' : money(r.balance)) }]}
    rows={data}
    rowActions={(r) => (r.txn_id ? [{ label: 'View/Edit', onClick: () => go('form', 'txndetail', { txnId: r.txn_id }) }] : [])} />;
}
